/**
 * Crew CSV Validation
 *
 * Zod schemas and validation utilities for crew member CSV imports.
 */

import { z } from 'zod';

// =============================================================================
// Column Definitions
// =============================================================================

export const CREW_CSV_COLUMNS = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'role',
] as const;

export const CREW_REQUIRED_COLUMNS = ['firstName', 'lastName', 'email'] as const;

// =============================================================================
// Row Schema
// =============================================================================

export const crewCsvRowSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(100, 'First name must be less than 100 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(100, 'Last name must be less than 100 characters'),
  email: z
    .string()
    .min(1, 'Email is required')
    .transform((val) => val.trim().toLowerCase())
    .pipe(z.string().email('Invalid email format')),
  phone: z
    .string()
    .max(20, 'Phone must be less than 20 characters')
    .optional()
    .default(''),
  role: z
    .string()
    .optional()
    .default('CREW')
    .transform((val) => val.toUpperCase())
    .pipe(z.enum(['CREW', 'ADMIN', 'OWNER'])),
});

export type CrewCSVRowInput = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role?: string;
};

export type CrewCSVRowOutput = z.output<typeof crewCsvRowSchema>;

// =============================================================================
// Validation Result
// =============================================================================

export interface CrewRowValidationResult {
  rowIndex: number;
  isValid: boolean;
  inputData: CrewCSVRowInput;
  parsedData: CrewCSVRowOutput | null;
  errors: string[];
}

// =============================================================================
// Validation Functions
// =============================================================================

export function validateCrewRow(
  row: CrewCSVRowInput,
  rowIndex: number
): CrewRowValidationResult {
  const result = crewCsvRowSchema.safeParse(row);

  if (result.success) {
    return {
      rowIndex,
      isValid: true,
      inputData: row,
      parsedData: result.data,
      errors: [],
    };
  }

  return {
    rowIndex,
    isValid: false,
    inputData: row,
    parsedData: null,
    errors: result.error.errors.map((e) => e.message),
  };
}

export function validateCrewRows(rows: CrewCSVRowInput[]): CrewRowValidationResult[] {
  const results = rows.map((row, index) => validateCrewRow(row, index));
  return markDuplicateEmails(results);
}

/**
 * Mark rows with duplicate emails within the CSV as invalid
 */
export function markDuplicateEmails(
  results: CrewRowValidationResult[]
): CrewRowValidationResult[] {
  const emailCounts = new Map<string, number[]>();

  // Collect email occurrences (use parsed email if valid, trimmed input otherwise)
  for (const r of results) {
    const email = r.parsedData?.email || r.inputData.email.trim().toLowerCase();
    if (!email) continue;
    const indices = emailCounts.get(email) || [];
    indices.push(r.rowIndex);
    emailCounts.set(email, indices);
  }

  // Mark duplicates
  for (const [, indices] of emailCounts) {
    if (indices.length > 1) {
      for (const idx of indices) {
        const r = results.find((r) => r.rowIndex === idx);
        if (r) {
          r.isValid = false;
          r.parsedData = null;
          r.errors.push('Duplicate email in CSV');
        }
      }
    }
  }

  return results;
}
